import { GateMovementMessageType as MessageType } from '@/__generated__/graphql';
import { readPayloadPosition } from '@features/gateCommon/utils/payloadPosition';
import { readMessageCode, type NotificationData } from './utils';

export type MovementGroup = {
  kind: 'movement';
  /** `id` del reporte más viejo: la tarjeta se actualiza en el lugar y no se anuncia como nueva. */
  key: number;
  count: number;
  /** Primera y última posición reportadas; `null` si ningún reporte trajo posición. */
  from: number | null;
  to: number | null;
  firstAt: number;
  lastAt: number;
  /** Es lo último que llegó y el movimiento no terminó: todavía se actualiza. */
  live: boolean;
  /** Código del reporte más reciente que trae uno (`GO06`, …), o `null`. */
  code: string | null;
};

export type FeedItem = { kind: 'message'; data: NotificationData } | MovementGroup;

export const feedItemKey = (item: FeedItem) => (item.kind === 'message' ? item.data.id : item.key);

/**
 * Funde los `MOVEMENT` **consecutivos** en un grupo. Otro tipo en medio corta el grupo, así que el
 * feed nunca reordena; un solo reporte queda como mensaje. `messages` va del más nuevo al más viejo
 * y la salida conserva ese orden. `finished` congela el grupo aunque sea lo último: hay cierres que
 * no publican mensaje.
 */
export const groupNotifications = (messages: NotificationData[], finished: boolean): FeedItem[] => {
  const items: FeedItem[] = [];
  let run: NotificationData[] = [];

  const flush = () => {
    if (run.length === 0) return;
    const latest = run[0];
    const oldest = run[run.length - 1];
    if (run.length === 1) {
      items.push({ kind: 'message', data: latest });
    } else {
      const positions = run.flatMap((message) => {
        const position = readPayloadPosition(message.payload);
        return position == null ? [] : [position];
      });
      items.push({
        kind: 'movement',
        key: oldest.id,
        count: run.length,
        from: positions.length ? positions[positions.length - 1] : null,
        to: positions.length ? positions[0] : null,
        firstAt: oldest.timestamp,
        lastAt: latest.timestamp,
        live: items.length === 0 && !finished,
        code: run.map(readMessageCode).find((code) => code != null) ?? null,
      });
    }
    run = [];
  };

  for (const message of messages) {
    if (message.type === MessageType.Movement) {
      run.push(message);
    } else {
      flush();
      items.push({ kind: 'message', data: message });
    }
  }
  flush();
  return items;
};
